import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Clock, Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tooltips orientados al profesional médico con recomendaciones de configuración
const CATEGORY_TOOLTIPS = {
  resultados: "Los pacientes suelen esperar respuesta cuando reciben resultados de estudios. Configurar tiempos claros y breves ayuda a reducir ansiedad y mejorar la experiencia. Tiempo recomendado: 1 a 2 días.",
  seguimiento: "Estas consultas suelen formar parte del control habitual del tratamiento. Un tiempo de respuesta razonable ayuda a mantener continuidad en el cuidado.",
  medicacion: "Dudas sobre medicación o posibles efectos adversos suelen requerir respuestas más rápidas para brindar seguridad al paciente. Tiempo recomendado: 1 día.",
  recetas: "Renovación de medicación sin cambios. Sensible al tiempo: el paciente puede quedarse sin droga. Tiempo recomendado: 48 horas hábiles.",
  documentacion: "Solicitudes administrativas como certificados o informes suelen tolerar tiempos de respuesta más amplios.",
  otra: "Este tipo de consultas puede requerir evaluación adicional. Definir un tiempo claro ayuda a ordenar las expectativas del paciente."
};

const SLAConfigPanel = ({ isOpen, onClose }) => {
  const [configs, setConfigs] = useState([]);
  const [timeOptions, setTimeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // category being saved

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sla-config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfigs(data.configs);
        setTimeOptions(data.time_options || []);
      } else {
        toast.error("Error al cargar configuración");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadConfigs();
    }
  }, [isOpen, loadConfigs]);

  const handleTimeChange = async (category, newHours) => {
    setSaving(category);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sla-config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category: category,
          response_hours: parseInt(newHours)
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        // Update local state
        setConfigs(prev => prev.map(c => 
          c.category === category 
            ? { ...c, response_hours: parseInt(newHours), is_custom: true }
            : c
        ));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al guardar");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error de conexión");
    } finally {
      setSaving(null);
    }
  };

  // Convert hours to display label
  const getTimeLabel = (hours) => {
    const option = timeOptions.find(opt => opt.value === hours);
    if (option) return option.label;
    
    // Fallback for legacy values
    if (hours < 24) return `${hours} horas`;
    const days = Math.round(hours / 24);
    return days === 1 ? "1 día" : `${days} días`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Tiempo de respuesta esperado
          </DialogTitle>
          <DialogDescription>
            Configura cuánto tiempo tienes para responder cada tipo de consulta.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <div className="space-y-1">
              {/* Table Header */}
              <div className="grid grid-cols-2 gap-4 px-4 py-3 bg-slate-50 rounded-t-lg border border-slate-200">
                <div className="text-sm font-medium text-slate-600">Tipo de consulta</div>
                <div className="text-sm font-medium text-slate-600">Tiempo de respuesta</div>
              </div>

              {/* Table Rows */}
              <div className="border border-t-0 border-slate-200 rounded-b-lg divide-y divide-slate-100">
                {configs.map((config) => (
                  <div
                    key={config.category}
                    className="grid grid-cols-2 gap-4 px-4 py-3 items-center hover:bg-slate-50 transition-colors"
                  >
                    {/* Consultation Type with Tooltip */}
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-900">
                        {config.category_label}
                      </p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            type="button"
                            className="text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                            aria-label={`Información sobre ${config.category_label}`}
                          >
                            <Info className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="top" 
                          align="start"
                          sideOffset={8}
                          collisionPadding={16}
                          className="max-w-[260px] bg-slate-800 text-white text-xs leading-relaxed p-3 shadow-lg"
                        >
                          {CATEGORY_TOOLTIPS[config.category]}
                        </TooltipContent>
                      </Tooltip>
                    </div>

                    {/* Time Selector */}
                    <div className="flex items-center gap-2">
                      <Select
                        value={config.response_hours?.toString()}
                        onValueChange={(value) => handleTimeChange(config.category, value)}
                        disabled={saving === config.category}
                      >
                        <SelectTrigger className="w-full h-9" data-testid={`time-select-${config.category}`}>
                          {saving === config.category ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Guardando...</span>
                            </div>
                          ) : (
                            <SelectValue placeholder="Seleccionar">
                              {getTimeLabel(config.response_hours)}
                            </SelectValue>
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.map((option) => (
                            <SelectItem 
                              key={option.value} 
                              value={option.value.toString()}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>

              {/* Info text */}
              <p className="text-xs text-slate-500 mt-4 px-1">
                Los cambios se guardan automáticamente al seleccionar un nuevo tiempo.
                La opción "15 días a 1 mes" es útil para ausencias temporales.
              </p>
            </div>
          </TooltipProvider>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SLAConfigPanel;
